"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001";

type FieldErrors = Partial<Record<"full_name" | "email" | "company" | "role" | "use_case", string>>;

export function RequestAccessForm() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [useCase, setUseCase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});

    if (!fullName.trim() || !email.trim() || !company.trim()) {
      setErrors({
        full_name: !fullName.trim() ? "Required" : undefined,
        email: !email.trim() ? "Required" : undefined,
        company: !company.trim() ? "Required" : undefined,
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/request-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName.trim(),
          email: email.trim(),
          company: company.trim(),
          role: role.trim() || null,
          use_case: useCase.trim() || null,
        }),
      });
      if (!res.ok) {
        if (res.status === 422) {
          toast.error("Please double-check the email address and try again.");
        } else if (res.status === 429) {
          toast.error("Too many requests — please try again in a few minutes.");
        } else {
          const text = await res.text();
          toast.error(`Could not submit: ${text.slice(0, 200)}`);
        }
        return;
      }
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch within 1–2 business days.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Network error";
      toast.error(`Could not submit: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="request-access" className="scroll-mt-20 border-t border-border/60 bg-secondary/15 py-20 sm:py-24">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Request Access
        </h2>
        <p className="mt-3 text-base text-foreground/75 sm:text-[17px]">
          Tell us a little about your product and team. We&apos;ll follow up with next steps for approved Suntheanine® customers and authorized brand partners.
        </p>
        <Card className="mt-8 border-border/70 shadow-sm">
          <CardHeader />
          <CardContent>
            {submitted ? (
              <div className="space-y-3 text-center">
                <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Send className="size-5" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Request received</h3>
                <p className="text-sm text-foreground/70">
                  Thanks, {fullName.split(" ")[0] || "there"}. We&apos;ll be in touch within 1–2 business days at <strong>{email}</strong>.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ra-full-name">Full name</Label>
                    <Input
                      id="ra-full-name"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      aria-invalid={errors.full_name ? true : undefined}
                    />
                    {errors.full_name ? <p className="text-xs text-destructive">{errors.full_name}</p> : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ra-email">Work email</Label>
                    <Input
                      id="ra-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      aria-invalid={errors.email ? true : undefined}
                    />
                    {errors.email ? <p className="text-xs text-destructive">{errors.email}</p> : null}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ra-company">Company</Label>
                    <Input
                      id="ra-company"
                      autoComplete="organization"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                      aria-invalid={errors.company ? true : undefined}
                    />
                    {errors.company ? <p className="text-xs text-destructive">{errors.company}</p> : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ra-role">Role <span className="text-muted-foreground">(optional)</span></Label>
                    <Input
                      id="ra-role"
                      autoComplete="organization-title"
                      placeholder="e.g. VP Marketing"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ra-use-case">
                    Product or use case <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Textarea
                    id="ra-use-case"
                    rows={4}
                    placeholder="Tell us about your finished product, formula, and the marketing content you're working on."
                    value={useCase}
                    onChange={(e) => setUseCase(e.target.value)}
                  />
                </div>
                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    For approved Suntheanine® customers and authorized brand partners.
                  </p>
                  <Button type="submit" disabled={submitting} className="gap-2">
                    {submitting ? <><Loader2 className="size-4 animate-spin" /> Sending…</> : <><Send className="size-4" /> Send Request</>}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
